import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/http/api.service';
import { IOcbApiDescriptionParameter, IOcbApiStringListParameter, IOcbCUFileTransfer, IOcbCUFileTransferRecipient, IOcbFileTransfer, IOcbFileTransferApiKey, IOcbFileTransferInfo, IOcbFileTransferLog, IOcbFileTransferRecipient, IOcbUser, OcbFileTransferLogTypes, OcbRemotePlatformLogTypes } from '@app/core/models';
import { IOcbApiStringParameter } from '@app/core/models/api/IOcbApiStringParameter';
import { IOcbCUFileTransferRenamingParameter } from '@app/core/models/api/IOcbCUFileTransferRenamingParameter';
import { IOcbFileTransferRenamingParameter } from '@app/core/models/api/IOcbFileTransferRenamingParameter';
import { IOcbLocalAgentCommand } from '@app/core/models/api/IOcbLocalAgentCommand';
import { environment } from '@env/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';
import { ImageService } from './image.service';

@Injectable()
export class FileTransfersService extends ApiService {

    private fileTransfersUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient, private imageService: ImageService) {
        super(httpClient);

        if (!environment.api.endpoint) {
            throw new Error('Could not initialize the "file-transfers" service, the proxyPath configuration is invalid.');
        }

        this.fileTransfersUrl = `${environment.api.endpoint}/FileTransfers`;
    }

    /**
     * Get the list of file transfers
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled {true} to return disabled file transfers
     */
    public getFileTransfers(environmentId?: string, includeDisabled?: boolean): Observable<IOcbFileTransfer[]> {

        let url = `${this.fileTransfersUrl}?`;

        if (environmentId) { url = `${url}EnvironmentId=${environmentId}&`; }
        if (includeDisabled) { url = `${url}IncludeDisabled=${includeDisabled}`; }

        return this.httpClient.get<IOcbFileTransfer[]>(url, this.options);
    }

    /**
     * Get the list of file transfers
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled {true} to return disabled file transfers
     */
    public getFileTransfersInfo(environmentId?: string, includeDisabled?: boolean): Observable<IOcbFileTransferInfo[]> {

        let url = `${this.fileTransfersUrl}/info?`;

        if (environmentId) { url = `${url}EnvironmentId=${environmentId}&`; }
        if (includeDisabled) { url = `${url}IncludeDisabled=${includeDisabled}`; }

        return this.httpClient.get<IOcbFileTransferInfo[]>(url, this.options);
    }

    /**
     * Create a file transfer
     * @param fileTransfer File transfer to create
     */
    public addFileTransfer(fileTransfer: IOcbCUFileTransfer): Observable<IOcbFileTransfer> {
        return this.httpClient.post<IOcbFileTransfer>(this.fileTransfersUrl, fileTransfer, this.options);
    }

    /**
     * Copy a file transfer
     * @param id Identifier of the File transfer to copy
     */
    public copyFileTransfer(id: string): Observable<IOcbFileTransfer> {
        return this.httpClient.post<IOcbFileTransfer>(`${this.fileTransfersUrl}/${id}/Copy`, this.options);
    }

    /**
     * Get a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public getFileTransfer(fileTransferId: string): Observable<IOcbFileTransfer> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}`;
        return this.httpClient.get<IOcbFileTransfer>(url, this.options);
    }

    /**
     * Update a file transfer
     * @param fileTransferId Internal identifier of the file transfer to update
     * @param fileTransfer File transfer to update
     */
    public updateFileTransfer(fileTransferId: string, fileTransfer: IOcbCUFileTransfer): Observable<IOcbFileTransfer> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}`;
        return this.httpClient.put<IOcbFileTransfer>(url, fileTransfer, this.options);
    }

    /**
     * Delete a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public deleteFileTransfer(fileTransferId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Disable a file transfer
     * @param fileTransferId Internal identifier of the a file transfer
     */
    public disableFileTransfer(fileTransferId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Disable`;
        return this.httpClient.post<void>(url, this.options);
    }

    /**
     * Enable a file transfer
     * @param fileTransferId Internal identifier of the a file transfer
     */
    public enableFileTransfer(fileTransferId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Enable`;
        return this.httpClient.post<void>(url, this.options);
    }

    // API keys

    /**
     * Get the list of Api Keys of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public getApiKeys(fileTransferId: string): Observable<IOcbFileTransferApiKey[]> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys`;
        return this.httpClient.get<IOcbFileTransferApiKey[]>(url, this.options);
    }

    /**
     * Create an Api Key for a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param description Description of the Api Key
     */
    public addApiKey(fileTransferId: string, description: IOcbApiDescriptionParameter): Observable<IOcbFileTransferApiKey> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys`;
        return this.httpClient.post<IOcbFileTransferApiKey>(url, description, this.options);
    }

    /**
     * Get an Api Key of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param apiKeyId Internal identifier of the Api Key
     */
    public getApiKey(fileTransferId: string, apiKeyId: string): Observable<IOcbFileTransferApiKey> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.get<IOcbFileTransferApiKey>(url, this.options);
    }

    /**
     * Update the description of an Api Key of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param apiKeyId Internal identifier of the Api Key
     * @param description New description
     */
    public updateApiKey(fileTransferId: string, apiKeyId: string, description: IOcbApiDescriptionParameter): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.put<void>(url, description, this.options);
    }

    /**
     * Reset an Api Key of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param apiKeyId Internal identifier of the Api Key
     */
    public resetApiKey(fileTransferId: string, apiKeyId: string): Observable<IOcbFileTransferApiKey> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.post<IOcbFileTransferApiKey>(url, this.options);
    }

    /**
     * Delete an Api Key of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param apiKeyId Internal identifier of the Api Key
     */
    public deleteApiKey(fileTransferId: string, apiKeyId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ApiKeys/${apiKeyId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // Archives files accesses

    /**
     * Authorize an access to archived files of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param employeeId EDA Internal identifier of the employee to authorize
     */
    public addArchivedFilesAccess(fileTransferId: string, employeeId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ArchivedFilesAccesses/${employeeId}`;
        return this.httpClient.post<void>(url, this.options);
    }

    /**
     * Delete an access to archived files of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param employeeId EDA Internal identifier of the employee to delete from accesses
     */
    public deleteArchivedFilesAccess(fileTransferId: string, employeeId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ArchivedFilesAccesses/${employeeId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Get the list of employees authorized to access archived files of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public getArchivedFilesAccess(fileTransferId: string): Observable<IOcbUser[]> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/ArchivedFilesAccesses`;
        return this.httpClient.get<IOcbUser[]>(url, this.options)
            .pipe(flatMap((users: IOcbUser[]) => {
                if (users.length > 0) {
                    return forkJoin(
                        users.map((user: IOcbUser) => {
                            return forkJoin([
                                this.httpClient.get(`https://graph.microsoft.com/beta/users/${user.id}`),
                                this.imageService.get(user.id).pipe(
                                    catchError(_ => of('../../assets/images/no-user-photo.jpg')),
                                ),
                            ])
                                .pipe(map((data: any[]) => {
                                    user.properties = data[0];
                                    user.photo = data[1];
                                    return user;
                                }));
                        }),
                    );
                }
                else return of([] as IOcbUser[]);
            }));
    }

    // Recipients

    // Renaming parameters

    /**
     * Get the list of renaming parameters of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public getRenamingParameters(fileTransferId: string): Observable<IOcbFileTransferRenamingParameter[]> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters`;
        return this.httpClient.get<IOcbFileTransferRenamingParameter[]>(url, this.options);
    }

    /**
     * Add a renaming parameter for a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param renamingParameter Renaming parameter to create
     */
    public addRenamingParameter(fileTransferId: string, renamingParameter: IOcbCUFileTransferRenamingParameter): Observable<IOcbFileTransferRenamingParameter> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters`;
        return this.httpClient.post<IOcbFileTransferRenamingParameter>(url, renamingParameter, this.options);
    }

    /**
     * Get a renaming parameter of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param renamingParameterId Internal identifier of the renaming parameter
     */
    public getRenamingParameter(fileTransferId: string, renamingParameterId: string): Observable<IOcbFileTransferRenamingParameter> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters/${renamingParameterId}`;
        return this.httpClient.get<IOcbFileTransferRenamingParameter>(url, this.options);
    }

    /**
     * Update a renaming parameter
     * @param fileTransferId Internal identifier of the file transfer
     * @param renamingParameterId Internal identifier of the renaming parameter to update
     * @param renamingParameter New renaming parameter’s settings
     */
    public updateRenamingParameter(fileTransferId: string, renamingParameterId: string, renamingParameter: IOcbCUFileTransferRenamingParameter): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters/${renamingParameterId}`;
        return this.httpClient.put<void>(url, renamingParameter, this.options);
    }

    /**
     * Delete a renaming parameter of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param renamingParameterId Internal identifier of the renaming parameter to delete
     */
    public deleteRenamingParameter(fileTransferId: string, renamingParameterId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters/${renamingParameterId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    /**
     * Test the renaming parameters of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param filename File name to test
     */
    public testRenamingParameters(fileTransferId: string, filename: IOcbApiStringParameter): Observable<string> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/RenamingParameters/Test`;
        return this.httpClient.post<string>(url, filename, this.options);
    }

    // RECIPIENTS

    /**
     * Get the list of recipients of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     */
    public getRecipients(fileTransferId: string): Observable<IOcbFileTransferRecipient[]> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Recipients`;
        return this.httpClient.get<IOcbFileTransferRecipient[]>(url, this.options).pipe(
            map(recipients => recipients.map(rec => this.mapRecipientLogTypesToEnums(rec))),
        );
    }

    /**
     * Create a recipient for a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param recipient Recipient to create
     */
    public addRecipient(fileTransferId: string, recipient: IOcbCUFileTransferRecipient): Observable<IOcbFileTransferRecipient> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Recipients`;
        recipient = this.mapRecipientLogTypesToNumbers(recipient);
        return this.httpClient.post<IOcbFileTransferRecipient>(url, recipient, this.options).pipe(
            map(newRecipient => this.mapRecipientLogTypesToEnums(newRecipient)),
        );
    }

    /**
     * Internal identifier of the file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param recipientId Internal identifier of the recipient to update
     */
    public getRecipient(fileTransferId: string, recipientId: string): Observable<IOcbFileTransferRecipient> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Recipients/${recipientId}`;
        return this.httpClient.get<IOcbFileTransferRecipient>(url, this.options).pipe(
            map(recipient => this.mapRecipientLogTypesToEnums(recipient)),
        );
    }

    /**
     * Update a recipient
     * @param fileTransferId Internal identifier of the file transfer
     * @param recipientId Internal identifier of the recipient to update
     * @param recipient New recipient’s settings
     */
    public updateRecipient(fileTransferId: string, recipientId: string, recipient: IOcbCUFileTransferRecipient): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Recipients/${recipientId}`;
        recipient = this.mapRecipientLogTypesToNumbers(recipient);
        return this.httpClient.put<void>(url, recipient, this.options);
    }

    /**
     * Delete a recipient of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param recipientId Internal identifier of the recipient to delete
     */
    public deleteRecipient(fileTransferId: string, recipientId: string): Observable<void> {
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Recipients/${recipientId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // LOGS

    /**
     * Get a file transfer log
     * @param logId Internal identifier of the log
     */
    public getLog(logId: string): Observable<IOcbFileTransferLog> {

        let url = `${this.fileTransfersUrl}/Logs/${logId}`;
        return this.httpClient.get<IOcbFileTransferLog>(url, this.options).pipe(map(log => this.mapLogTypesToEnums(log)));
    }

    /**
     * Get the list of the logs of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getLogs(fileTransferId: string, utcDateFrom?: string, utcDateTo?: string): Observable<IOcbFileTransferLog[]> {

        let url = `${this.fileTransfersUrl}/${fileTransferId}/Logs?`;

        if (utcDateFrom) { url = `${url}UtcDateFrom=${utcDateFrom}&`; }
        if (utcDateTo) { url = `${url}UtcDateTo=${utcDateTo}`; }

        return this.httpClient.get<IOcbFileTransferLog[]>(url, this.options).pipe(
            map(logs => logs.map(log => this.mapLogTypesToEnums(log))),
        );
    }

    /**
     * Get the list of the logs of the file transfers of the environments for which the logged employee is administrator and has "can access logs" rights
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getAllLogs(utcDateFrom: string, utcDateTo: string, environmentId?: string): Observable<IOcbFileTransferLog[]> {

        let url = `${this.fileTransfersUrl}/Logs?`;

        if (utcDateFrom) { url = `${url}UtcDateFrom=${utcDateFrom}`; }
        if (utcDateTo) {
            if (!url.endsWith("?")) { url = url + "&"; }
            url = `${url}UtcDateTo=${utcDateTo}`;
        }
        if (environmentId) {
            if (!url.endsWith("?")) { url = url + "&"; }
            url = `${url}EnvironmentId=${environmentId}`;
        }

        return this.httpClient.get<IOcbFileTransferLog[]>(url, this.options).pipe(
            map(logs => logs.map(log => this.mapLogTypesToEnums(log))),
        );
    }
    
    // COMMANDS

    /**
     * Request the checking of the connection to the source remote platform of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public checkConnectionSource(fileTransferId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/CheckConnectionSource?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Request the checking of the connection to the target remote platform of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public checkConnectionTarget(fileTransferId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/CheckConnectionTarget?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Request the getting of the available files on the source (folder or remote platform)
     * @param fileTransferId Internal identifier of the file transfer
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public getListFiles(fileTransferId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/ListFiles?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Request the execution of a file transfer
     * @param fileTransferId Internal identifier of the file transfer
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public executeFileTransfer(fileTransferId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/Execute?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Request the execution of all file transfers on an environment
     * @param environmentId Internal identifier of the environment
     * @param waitForExecution {true} to wait for the execution of the commands (default : {false})
     */
    public executeFileTransfers(environmentId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand[]> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/Commands/Execute/${environmentId}?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand[]>(url, this.options);
    }

    /**
     * Send an archived file by email
     * @param fileTransferId Internal identifier of the file transfer
     * @param fileTransferLogId Internal identifier of the log of the file transfer (an archived file is associated to a log)
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     * @param employees List of EDA internal identifier of the employees who must receive the archived file
     */
    public sendArchivedFile(fileTransferId: string, fileTransferLogId: string, waitForExecution?: boolean, employees?: IOcbApiStringListParameter): Observable<IOcbLocalAgentCommand[]> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/SendArchivedFile/${fileTransferLogId}?WaitForExecution=${waitForExecution}`;

        if (!employees) { employees = {}; }

        return this.httpClient.post<IOcbLocalAgentCommand[]>(url, employees, this.options);
    }

    /**
     * Delete an archived file by email
     * @param fileTransferId Internal identifier of the file transfer
     * @param fileTransferLogId Internal identifier of the log of the file transfer (an archived file is associated to a log)
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public deleteArchivedFile(fileTransferId: string, fileTransferLogId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.fileTransfersUrl}/${fileTransferId}/Commands/DeleteArchivedFile/${fileTransferLogId}?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    // MAPPINGS

    /**
     * Map remote platform recipients enum numbers to enum arrays.
     */
    private mapRecipientLogTypesToEnums(recipient: IOcbFileTransferRecipient): IOcbFileTransferRecipient {
        recipient.fileTransferLogTypesEnums = this.splitEnums(recipient.fileTransferLogTypes, OcbFileTransferLogTypes);
        recipient.remotePlatformLogTypesEnums = this.splitEnums(recipient.remotePlatformLogTypes, OcbRemotePlatformLogTypes);
        return recipient;
    }

    /**
     * Map remote platform recipients enum arrays to enum numbers.
     */
    private mapRecipientLogTypesToNumbers(recipient: IOcbCUFileTransferRecipient): IOcbCUFileTransferRecipient {
        recipient.fileTransferLogTypes = this.combineEnums(recipient.fileTransferLogTypesEnums, OcbFileTransferLogTypes);
        recipient.remotePlatformLogTypes = this.combineEnums(recipient.remotePlatformLogTypesEnums, OcbRemotePlatformLogTypes);
        return recipient;
    }

    /**
     * Map logs enum numbers to enum arrays.
     */
    private mapLogTypesToEnums(log: IOcbFileTransferLog): IOcbFileTransferLog {
        log.logTypeEnums = this.splitEnums(log.logType, OcbFileTransferLogTypes);
        return log;
    }
}
